import { Injectable } from '@angular/core';
import { Firestore, collection, query, where, getDocs, doc, getDoc, updateDoc, addDoc, deleteField, deleteDoc, writeBatch, setDoc } from '@angular/fire/firestore';
import { BehaviorSubject } from 'rxjs';
import { getAuth } from 'firebase/auth';

export type SupportedLanguage = 'en' | 'nl' | 'fr' | 'de' | 'es' | 'it';

interface Website {
  domain: string;
  title: string;
  route: string;
  children?: Website[];
  createdAt: Date;
  order: number;
}

export interface ComponentData {
  [key: string]: any;
}

export interface PageComponent {
  type: string;
  data: ComponentData;
}

export interface Page {
  id: string;
  content: string;
  components?: PageComponent[];
  createdAt: Date;
  domain: string;
  language: SupportedLanguage;
  order: number;
  parentId: string | null;
  route: string;
  title: string;
  updatedAt: Date;
  templateType?: string;
  categories?: string[];
  basePageId?: string; // Links pages in different languages together
  authorId?: string; // User ID of the author
  published?: boolean; // Whether the page is published and visible in menu
  isHomepage?: boolean; // Whether this page is the homepage (root /) for this domain and language
  // External link fields
  isExternalLink?: boolean;
  externalUrl?: string;
  openInNewTab?: boolean;
  // Menu display type
  menuDisplayType?: 'default' | 'mega' | 'advertising';
  menuHighlight?: {
    image?: string;
    title?: string;
    description?: string;
    ctaText?: string;
    ctaUrl?: string;
  };
}

export interface PageWithChildren extends Page {
  children?: PageWithChildren[];
}

export interface SiteLanguages {
  id?: string;
  domain: string;
  languages: string[];
  updatedAt: Date;
}

export interface Domain {
  id: string;
  name: string;
  createdAt: Date;
  updatedAt?: Date;
}

interface StoredDomainData {
  name: string;
  id: string;
  createdAt: Date;
  updatedAt?: Date;
}

export interface LanguageOption {
  code: string;
  name: string;
}

export interface WebsiteSettings {
  domain: string;
  languages?: string[];
  defaultLanguage?: string;
  seo?: {
    defaultTitle: string;
    defaultDescription: string;
    defaultKeywords: string[];
  };
  availableLanguages: LanguageOption[];
}

export interface Post {
  id: string;
  categoryId: string;
  // ... other post properties
}

export interface Category {
  id: string;
  name: string;
  slug?: string;
  parentId?: string | null;
  translations?: Record<string, string>; // { en: 'Products', de: 'Produkte', ... }
}


@Injectable({
  providedIn: 'root'
})
export class WebsiteService {
  private readonly STORAGE_KEY = 'dwt_last_domain';
  private readonly LANGUAGE_STORAGE_KEY = 'dwt_last_language';
  private currentDomainSubject = new BehaviorSubject<string>('');
  private domains: Domain[] = [];
  private domainChangeSubject = new BehaviorSubject<void>(undefined);
  private currentLanguageSubject = new BehaviorSubject<SupportedLanguage>('en');

  /** In-memory caches om herhaalde Firestore calls te voorkomen */
  private settingsCache = new Map<string, { data: WebsiteSettings | null; ts: number }>();
  private categoriesCache = new Map<string, { data: Category[]; ts: number }>();
  private domainsCache: { data: Domain[]; ts: number } | null = null;
  private readonly CACHE_TTL = 60_000; // 60 seconden

  currentDomain$ = this.currentDomainSubject.asObservable();
  domainChange$ = this.domainChangeSubject.asObservable();
  currentLanguage$ = this.currentLanguageSubject.asObservable();

  constructor(private firestore: Firestore) {
    // Try to restore last domain from storage
    const storedData = localStorage.getItem(this.STORAGE_KEY);
    if (storedData) {
      try {
        const domainData: StoredDomainData = JSON.parse(storedData);
        this.currentDomainSubject.next(domainData.name);
      } catch (e) {
        console.warn('Failed to parse stored domain data');
      }
    }

    // Restore last selected language from storage
    const storedLang = localStorage.getItem(this.LANGUAGE_STORAGE_KEY);
    if (storedLang) {
      this.currentLanguageSubject.next(storedLang as SupportedLanguage);
    }
  }

  getCurrentDomain(): string {
    return this.currentDomainSubject.value;
  }

  getStoredDomainData(): StoredDomainData | null {
    const storedData = localStorage.getItem(this.STORAGE_KEY);
    if (storedData) {
      try {
        return JSON.parse(storedData);
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  async setCurrentDomain(domainName: string) {
    if (!domainName) return;
    
    try {
      if (this.domains.length === 0) {
        await this.getDomains();
      }

      const domainData = this.domains.find((d: Domain) => d.name === domainName);
      
      if (domainData) {
        const storedData: StoredDomainData = {
          name: domainData.name,
          id: domainData.id,
          createdAt: domainData.createdAt,
          updatedAt: domainData.updatedAt
        };
        
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(storedData));
        this.currentDomainSubject.next(domainName);
        this.domainChangeSubject.next();
      }
    } catch (error) {
      console.error('Error setting current domain:', error);
      throw error;
    }
  }

  async getWebsites(): Promise<string[]> {
    try {
      const domainsRef = collection(this.firestore, 'domains');
      const snapshot = await getDocs(domainsRef);
      
      return snapshot.docs
        .map(doc => doc.data()['name'] as string)
        .filter(name => name) // Filter out any undefined/null values
        .sort(); // Sort alphabetically
    } catch (error) {
      console.error('Error getting websites:', error);
      return [];
    }
  }

  async getPagesByDomain(domain: string, language?: SupportedLanguage): Promise<Page[]> {
    if (!domain) {
      console.warn('No domain provided to getPagesByDomain');
      return [];
    }

    const pagesRef = collection(this.firestore, 'pages');
    let q;
    if (language) {
      q = query(pagesRef, where('domain', '==', domain), where('language', '==', language));
    } else {
      q = query(pagesRef, where('domain', '==', domain));
    }
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        createdAt: data['createdAt']?.toDate(),
        updatedAt: data['updatedAt']?.toDate(),
        published: data['published'] !== undefined ? data['published'] : false,
      isHomepage: data['isHomepage'] === true
      } as Page;
    }).sort((a, b) => a.order - b.order);
  }

  async getPageById(pageId: string): Promise<Page | null> {
    const currentDomain = this.getCurrentDomain();
    if (!currentDomain) return null;

    const pageRef = doc(this.firestore, 'pages', pageId);
    const pageSnap = await getDoc(pageRef);

    if (!pageSnap.exists()) return null;

    const data = pageSnap.data();
    if (data['domain'] !== currentDomain) return null;

    return {
      ...data,
      id: pageSnap.id,
      createdAt: data['createdAt']?.toDate(),
      updatedAt: data['updatedAt']?.toDate(),
      content: data['content'] || '',
      title: data['title'] || '',
      parentId: data['parentId'] || null,
      order: data['order'] || 0,
      language: data['language'] || 'en',
      published: data['published'] !== undefined ? data['published'] : false,
      isHomepage: data['isHomepage'] === true
    } as Page;
  }

  async getPageByRoute(route: string, locale: SupportedLanguage = 'en'): Promise<Page | null> {
    const currentDomain = this.getCurrentDomain();
    if (!currentDomain) return null;

    const pages = await this.getPagesByDomain(currentDomain, locale);
    
    // Find page by route in the specified language
    const page = pages.find(p => p.route === route && p.language === locale);

    return page || null;
  }

  async updatePage(page: Page): Promise<void> {
    const currentDomain = this.getCurrentDomain();
    if (!currentDomain || page.domain !== currentDomain) {
      throw new Error('Invalid domain for page update');
    }

    const auth = getAuth();
    const currentUser = auth.currentUser;
    const authorId = currentUser?.uid || page.authorId || null;

    const pageRef = doc(this.firestore, 'pages', page.id);
    const { id, ...pageData } = page;  // Remove id before saving
    
    // Ensure all required fields are present and remove undefined values
    const updateData: any = {
      content: pageData.content || '',
      title: pageData.title || '',
      language: pageData.language || 'en',
      route: pageData.route || '',
      domain: pageData.domain,
      order: pageData.order ?? -1,
      parentId: pageData.parentId ?? null,
      createdAt: pageData.createdAt || new Date(),
      updatedAt: new Date()
    };

    // Only add optional fields if they are defined
    if (pageData.components !== undefined && pageData.components !== null) {
      updateData.components = pageData.components;
    }
    if (pageData.templateType !== undefined && pageData.templateType !== null) {
      updateData.templateType = pageData.templateType;
    }
    if (pageData.categories !== undefined && pageData.categories !== null) {
      updateData.categories = pageData.categories;
    }
    if (pageData.basePageId !== undefined && pageData.basePageId !== null) {
      updateData.basePageId = pageData.basePageId;
    }
    if (pageData.published !== undefined) {
      updateData.published = pageData.published;
    }
    if (pageData.isHomepage !== undefined) {
      updateData.isHomepage = pageData.isHomepage;
    }
    // External link fields
    if (pageData.isExternalLink !== undefined) {
      updateData.isExternalLink = pageData.isExternalLink;
    }
    if (pageData.externalUrl !== undefined) {
      updateData.externalUrl = pageData.externalUrl;
    }
    if (pageData.openInNewTab !== undefined) {
      updateData.openInNewTab = pageData.openInNewTab;
    }
    // Menu display type
    if (pageData.menuDisplayType !== undefined) {
      updateData.menuDisplayType = pageData.menuDisplayType;
    }
    if (pageData.menuHighlight !== undefined) {
      updateData.menuHighlight = pageData.menuHighlight;
    }
    if (authorId !== null) {
      updateData.authorId = authorId;
    }

    return updateDoc(pageRef, updateData);
  }

  async setHomepage(pageId: string, domain: string, language: SupportedLanguage): Promise<void> {
    // First, unset all other homepages for this domain and language
    const pages = await this.getPagesByDomain(domain, language);
    const otherHomepages = pages.filter(p => p.id !== pageId && p.isHomepage === true);
    
    // Update all other homepages to false
    const batch = writeBatch(this.firestore);
    for (const page of otherHomepages) {
      const pageRef = doc(this.firestore, 'pages', page.id);
      batch.update(pageRef, { isHomepage: false, updatedAt: new Date() });
    }
    
    // Set the selected page as homepage
    const pageRef = doc(this.firestore, 'pages', pageId);
    batch.update(pageRef, { isHomepage: true, updatedAt: new Date() });
    
    await batch.commit();
  }

  async unsetHomepage(pageId: string): Promise<void> {
    const pageRef = doc(this.firestore, 'pages', pageId);
    await updateDoc(pageRef, { isHomepage: false, updatedAt: new Date() });
  }

  async createPage(
    domain: string,
    title: string,
    route: string,
    language: SupportedLanguage,
    order: number = -1,
    basePageId?: string
  ): Promise<string> {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    const authorId = currentUser?.uid || null;

    const pagesRef = collection(this.firestore, 'pages');
    const newPage = {
      domain,
      title,
      route,
      content: '',
      language,
      createdAt: new Date(),
      updatedAt: new Date(),
      order,
      parentId: null,
      basePageId: basePageId || null,
      authorId: authorId || null,
      published: false // New pages are not published by default
    };

    const docRef = await addDoc(pagesRef, newPage);
    
    return docRef.id;
  }

  async getMenuStructure(domain: string, language: SupportedLanguage = 'en'): Promise<PageWithChildren[]> {
    try {
      // Get all pages for the domain and language
      const pages = await this.getPagesByDomain(domain, language);
      
      // Filter to only pages that are in the menu (order >= 0), regardless of published status
      const menuPages = pages.filter(page => page.order >= 0);
      
      // Sort by order
      menuPages.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      // Create a map of pages by ID for quick lookup
      const pageMap = new Map<string, PageWithChildren>();
      menuPages.forEach(page => {
        pageMap.set(page.id, { ...page, children: [] });
      });
      
      // Build the tree structure based on parentId and order
      const rootPages: PageWithChildren[] = [];
      
      menuPages.forEach(page => {
        const pageWithChildren = pageMap.get(page.id)!;
        if (page.parentId && pageMap.has(page.parentId)) {
          // This page has a parent, add it to parent's children
          const parent = pageMap.get(page.parentId)!;
          if (!parent.children) {
            parent.children = [];
          }
          parent.children.push(pageWithChildren);
        } else {
          // This is a root page
          rootPages.push(pageWithChildren);
        }
      });
      
      // Sort children within each parent
      const sortChildren = (pages: PageWithChildren[]) => {
        pages.sort((a, b) => (a.order || 0) - (b.order || 0));
        pages.forEach(page => {
          if (page.children && page.children.length > 0) {
            sortChildren(page.children);
          }
        });
      };
      sortChildren(rootPages);
      
      return rootPages;
    } catch (error) {
      console.error('Error getting menu structure:', error);
      throw error;
    }
  }


  async updatePageOrder(pageId: string, newOrder: number, newParentId: string | null = null): Promise<void> {
    const pageRef = doc(this.firestore, 'pages', pageId);
    return updateDoc(pageRef, {
      order: newOrder,
      parentId: newParentId,
      updatedAt: new Date()
    });
  }

  async getLanguages(domain: string): Promise<string[]> {
    const languagesRef = collection(this.firestore, 'languages');
    const q = query(languagesRef, where('domain', '==', domain));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      const defaultLangs = ['en', 'nl'];
      await this.saveLanguages(domain, defaultLangs);
      return defaultLangs;
    }

    return snapshot.docs[0].data()['languages'];
  }

  private async saveLanguages(domain: string, languages: string[]): Promise<void> {
    const languagesRef = collection(this.firestore, 'languages');
    const q = query(languagesRef, where('domain', '==', domain));
    const snapshot = await getDocs(q);

    const data: SiteLanguages = {
      domain,
      languages,
      updatedAt: new Date()
    };

    if (snapshot.empty) {
      await addDoc(languagesRef, data);
    } else {
      await updateDoc(doc(this.firestore, 'languages', snapshot.docs[0].id), data as any);
    }
  }

  async addLanguage(lang: string): Promise<void> {
    const domain = this.getCurrentDomain();
    if (!domain) return;

    // Get all pages for the default language (usually 'en')
    const defaultPages = await this.getPagesByDomain(domain, 'en' as SupportedLanguage);
    
    // Create new page documents for the new language based on existing pages
    const pagePromises = defaultPages.map(async (defaultPage) => {
      // Check if page for this language already exists
      const existingPages = await this.getPagesByDomain(domain, lang as SupportedLanguage);
      const exists = existingPages.some(p => p.basePageId === defaultPage.id || p.basePageId === defaultPage.basePageId);
      
      if (!exists) {
        const basePageId = defaultPage.basePageId || defaultPage.id;
        await this.createPage(
          domain,
          defaultPage.title,
          defaultPage.route,
          lang as SupportedLanguage,
          defaultPage.order,
          basePageId
        );
      }
    });

    // Get current languages and add new one
    const currentLangs = await this.getLanguages(domain);
    if (!currentLangs.includes(lang)) {
      currentLangs.push(lang);
      await this.saveLanguages(domain, currentLangs);
    }

    await Promise.all(pagePromises);
  }

  async removeLanguage(lang: string): Promise<void> {
    const domain = this.getCurrentDomain();
    if (!domain) return;

    // Get all pages for the language to remove
    const pagesToDelete = await this.getPagesByDomain(domain, lang as SupportedLanguage);
    
    // Delete all pages for this language
    const deletePromises = pagesToDelete.map(page => {
      const pageRef = doc(this.firestore, 'pages', page.id);
      return deleteDoc(pageRef);
    });

    // Remove language from languages collection
    const currentLangs = await this.getLanguages(domain);
    const updatedLangs = currentLangs.filter(l => l !== lang);
    await this.saveLanguages(domain, updatedLangs);

    await Promise.all(deletePromises);
  }

  async getAllPages(): Promise<Page[]> {
    const currentDomain = this.getCurrentDomain();
    if (!currentDomain) return [];

    const pagesRef = collection(this.firestore, 'pages');
    const q = query(pagesRef, where('domain', '==', currentDomain));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    } as Page));
  }

  async deletePage(domain: string, pageId: string): Promise<void> {
    try {
      // Delete the page document from Firestore
      const pageRef = doc(this.firestore, 'pages', pageId);
      await deleteDoc(pageRef);

      // Reload pages for the domain after deletion
      await this.getPagesByDomain(domain);


    } catch (error) {
      console.error('Error deleting page:', error);
      throw error;
    }
  }

  async getDomains(): Promise<Domain[]> {
    const now = Date.now();
    if (this.domainsCache && now - this.domainsCache.ts < this.CACHE_TTL) {
      this.domains = this.domainsCache.data;
      return this.domains;
    }
    this.domains = await this.getDomainsFromFirestore();
    this.domainsCache = { data: this.domains, ts: now };
    return this.domains;
  }

  private async getDomainsFromFirestore(): Promise<Domain[]> {
    const domainsRef = collection(this.firestore, 'domains');
    const snapshot = await getDocs(domainsRef);
    return snapshot.docs.map(doc => ({ 
      ...doc.data(),
      id: doc.id,
      createdAt: doc.data()['createdAt']?.toDate(),
      updatedAt: doc.data()['updatedAt']?.toDate()
    } as Domain));
  }

  async addDomain(name: string): Promise<string> {
    const domainsRef = collection(this.firestore, 'domains');
    const domain: Omit<Domain, 'id'> = {
      name,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    const docRef = await addDoc(domainsRef, domain);
    return docRef.id;
  }

  async deleteDomain(domainId: string): Promise<void> {
    try {
      const domainRef = doc(this.firestore, 'domains', domainId);
      await deleteDoc(domainRef);
      
      // Remove from local domains array
      this.domains = this.domains.filter(d => d.id !== domainId);
      
      // If the deleted domain was the current one, switch to another or clear
      const storedDomainData = this.getStoredDomainData();
      if (storedDomainData?.id === domainId) {
        if (this.domains.length > 0) {
          await this.setCurrentDomain(this.domains[0].name);
        } else {
          localStorage.removeItem(this.STORAGE_KEY);
          this.currentDomainSubject.next('');
        }
      }
    } catch (error) {
      console.error('Error deleting domain:', error);
      throw error;
    }
  }

  // Add this method to check if a page belongs to the current domain
  async validatePageDomain(pageId: string): Promise<boolean> {
    const currentDomain = this.getCurrentDomain();
    if (!currentDomain) return false;

    const page = await this.getPageById(pageId);
    return page?.domain === currentDomain;
  }

  async getWebsiteSettings(domain: string): Promise<WebsiteSettings | null> {
    if (!domain) return null;

    const now = Date.now();
    const cached = this.settingsCache.get(domain);
    if (cached && now - cached.ts < this.CACHE_TTL) {
      return cached.data;
    }

    try {
      const settingsRef = doc(this.firestore, 'website_settings', domain);
      const settingsSnap = await getDoc(settingsRef);

      let result: WebsiteSettings | null;
      if (settingsSnap.exists()) {
        result = settingsSnap.data() as WebsiteSettings;
      } else {
        // Return default settings if none exist
        result = {
          domain,
          languages: ['en', 'nl'],
          defaultLanguage: 'en',
          availableLanguages: [
            { code: 'en', name: 'English' },
            { code: 'nl', name: 'Nederlands' }
          ]
        };
      }
      this.settingsCache.set(domain, { data: result, ts: now });
      return result;
    } catch (error) {
      console.error('Error getting website settings:', error);
      return null;
    }
  }

  async getCategories(domain: string): Promise<Category[]> {
    if (!domain) return [];

    const now = Date.now();
    const cached = this.categoriesCache.get(domain);
    if (cached && now - cached.ts < this.CACHE_TTL) {
      return cached.data;
    }

    const categoriesRef = collection(this.firestore, 'categories');
    const q = query(categoriesRef, where('domain', '==', domain));
    const categoriesSnapshot = await getDocs(q);

    const categories = categoriesSnapshot.docs.map(doc => ({
      id: doc.id,
      name: doc.data()['name'],
      slug: doc.data()['slug'] || '',
      parentId: doc.data()['parentId'] || null,
      translations: doc.data()['translations'] || {}
    }));

    const sorted = categories.sort((a, b) => a.name.localeCompare(b.name));
    this.categoriesCache.set(domain, { data: sorted, ts: now });
    return sorted;
  }

  /** Maakt een nieuwe categorie aan voor het gegeven domein. Retourneert het document-id. */
  async createCategory(domain: string, name: string, parentId?: string | null, slug?: string): Promise<string> {
    const n = (name || '').trim();
    if (!domain || !n) throw new Error('Domein en naam zijn verplicht');
    const categoriesRef = collection(this.firestore, 'categories');
    const data: Record<string, unknown> = {
      domain,
      name: n,
      slug: slug || this.generateSlug(n),
      createdAt: new Date()
    };
    if (parentId) data['parentId'] = parentId;
    const docRef = await addDoc(categoriesRef, data);
    this.categoriesCache.delete(domain);
    return docRef.id;
  }

  /** Update een categorie (naam, slug, translations). */
  async updateCategory(categoryId: string, name: string, domain: string, slug?: string, translations?: Record<string, string>): Promise<void> {
    const n = (name || '').trim();
    if (!categoryId || !n) throw new Error('ID en naam zijn verplicht');
    const catRef = doc(this.firestore, 'categories', categoryId);
    const updateData: { [key: string]: string | Record<string, string> } = { name: n };
    if (slug !== undefined) updateData['slug'] = slug;
    if (translations !== undefined) updateData['translations'] = translations;
    await updateDoc(catRef, updateData);
    this.categoriesCache.delete(domain);
  }

  /** Verwijder een categorie en verwijder de referenties uit posts. */
  async deleteCategory(categoryId: string, domain: string): Promise<void> {
    if (!categoryId) throw new Error('Category ID is verplicht');
    // Verwijder het categorie-document
    const catRef = doc(this.firestore, 'categories', categoryId);
    await deleteDoc(catRef);

    // Verplaats subcategorieën naar root (parentId verwijderen)
    const categoriesRef = collection(this.firestore, 'categories');
    const subQ = query(categoriesRef, where('parentId', '==', categoryId));
    const subSnap = await getDocs(subQ);
    const batch = writeBatch(this.firestore);
    for (const d of subSnap.docs) {
      batch.update(d.ref, { parentId: deleteField() });
    }
    if (!subSnap.empty) await batch.commit();

    this.categoriesCache.delete(domain);
  }

  /** Verplaats een categorie naar een andere parent (of naar root met null). */
  private generateSlug(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  /** Haal een categorie op basis van slug. */
  async getCategoryBySlug(domain: string, slug: string): Promise<Category | null> {
    const categories = await this.getCategories(domain);
    return categories.find(c => c.slug === slug) || null;
  }

  async moveCategory(categoryId: string, parentId: string | null, domain: string): Promise<void> {
    const catRef = doc(this.firestore, 'categories', categoryId);
    if (parentId) {
      await updateDoc(catRef, { parentId });
    } else {
      await updateDoc(catRef, { parentId: deleteField() });
    }
    this.categoriesCache.delete(domain);
  }

  /**
   * Kopieert de content van een bronpagina naar een doelpagina op een ander domein.
   * Omzeilt de domain-check van updatePage() omdat dit een legitieme cross-domain operatie is.
   */
  async copyPageContent(targetPageId: string, sourcePage: Page): Promise<void> {
    const pageRef = doc(this.firestore, 'pages', targetPageId);

    const updateData: any = {
      content: sourcePage.content || '',
      updatedAt: new Date()
    };

    if (sourcePage.components && sourcePage.components.length > 0) {
      updateData.components = JSON.parse(JSON.stringify(sourcePage.components));
    }
    if (sourcePage.templateType) {
      updateData.templateType = sourcePage.templateType;
    }
    if (sourcePage.categories && sourcePage.categories.length > 0) {
      updateData.categories = [...sourcePage.categories];
    }

    return updateDoc(pageRef, updateData);
  }

  setCurrentLanguage(lang: SupportedLanguage) {
    this.currentLanguageSubject.next(lang);
    localStorage.setItem(this.LANGUAGE_STORAGE_KEY, lang);
  }

  getCurrentLanguage(): SupportedLanguage {
    return this.currentLanguageSubject.value;
  }
} 